import trainingQualificationService from '@/modules/auth/services/TrainingQualificationService';
import type AvailableTrainingQualification from '@/types/AvailableTrainingQualification';
import type TrainingQualification from '@/types/TrainingQualification';

export default function useTrainingQualification() {
  const availableTrainingQualifications = ref<AvailableTrainingQualification[]>([]);
  const trainingQualifications = ref<{ abn: string; qualifications: TrainingQualification[] }>({ abn: '', qualifications: [] });
  const fetchingAvailable = ref(false);
  const fetching = ref(false);
  const creating = ref(false);

  const { showSnackbar } = useSnackbar();

  const getAvailableTrainingQualifications = async (): Promise<AvailableTrainingQualification[]> => {
    if (fetchingAvailable.value) return [];

    availableTrainingQualifications.value = [];
    fetchingAvailable.value = true;

    const { data } = await trainingQualificationService.getAvailableTrainingQualifications();

    availableTrainingQualifications.value = data.value || [];
    fetchingAvailable.value = false;

    return availableTrainingQualifications.value;
  };

  const getTrainingQualifications = async (): Promise<{ abn: string; qualifications: TrainingQualification[] }> => {
    if (fetching.value) return { abn: '', qualifications: [] };

    trainingQualifications.value = { abn: '', qualifications: [] };
    fetching.value = true;

    const { data } = await trainingQualificationService.getTrainingQualifications();

    // Store the full response structure to preserve ABN
    const fullResponse = data.value || { abn: '', qualifications: [] };
    trainingQualifications.value = fullResponse;
    
    fetching.value = false;

    return fullResponse;
  };

  const createTrainingQualifications = async (form: { abn: string; qualifications: TrainingQualification[] }) => {
    if (creating.value) return;

    creating.value = true;
    const { data, error } = await trainingQualificationService.createTrainingQualifications(form);
    creating.value = false;

    if (error.value) {
      showSnackbar({
        text: error.value,
        state: false,
      });
    } else {
      // @NOTE: API returns the whole `/me` object rather than the created object
      if (data.value?.data?.qualifications) {
        trainingQualifications.value = {
          abn: form.abn,
          qualifications: data.value.data.qualifications
        };
      }
      return trainingQualifications.value;
    }
  };

  return {
    availableTrainingQualifications,
    trainingQualifications,
    fetchingAvailable,
    fetching,
    creating,

    getAvailableTrainingQualifications,
    getTrainingQualifications,
    createTrainingQualifications,
  };
}
